<template>
  <el-dialog
    :visible.sync="openModal"
    title="Project Settings"
    width="800px"
    @close="reset">
    <form
      class="project-form"
      id="form"
      @submit.prevent="submit">
      <video-settings
        v-if="currentVideoId"
        ref="videoInfo"
        :details="videoDetails" />
      <div v-if="currentVideoId" class="divider" />
      <template v-for="(video, index) in videos">
        <div :key="video.video_id">
          <div class="form-row-header">
            <span class="form-row-name">{{ video.video_name }}</span>
            <span class="form-row-per-second">Ad price per second:</span>
            <span>{{ getValuePerSecond(video.price, video.length) }}</span>
          </div>
          <div class="form-row-content">
            <div class="form-field">
              <div class="form-field-label">Name</div>
              <el-input v-model="video.broadcast" />
            </div>
            <div class="form-field">
              <div class="form-field-label">Ad price</div>
              <div class="currency">
                <span class="currency-icon" :style="{ 'left': `${23 - 9 * currencyIcon.length}px` }">{{ currencyIcon }}</span>
                <el-input v-model="video.price" type="number" />
              </div>
            </div>
            <div class="form-field">
              <div class="form-field-label">Ad length</div>
              <el-select
                v-model="video.length"
                placeholder="Select length">
                <el-option
                  v-for="item in [1, 5, 10, 15, 30, 45, 60]"
                  :key="item"
                  :label="`${item} seconds`"
                  :value="item" />
              </el-select>
            </div>
          </div>
        </div>
        <div v-if="index !== videos.length - 1" class="divider" :key="video.video_id + 'divider'" />
      </template>
    </form>
    <template slot="footer">
      <el-button @click="openModal = false">Cancel</el-button>
      <el-button
        form="form"
        native-type="submit"
        type="primary"
        :loading="loading">
        Save
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import { mapActions } from 'vuex';
import { getProjectSettings, updateProjectSettings } from "@/api/projects";
import { updateVideo } from "@/api/files";
import VideoSettings from './VideoSettings';

const DEFAULT_VIDEO =  {
  broadcast: "",
  length: 'Not selected',
  price: '',
};

export default {
  components: { VideoSettings },
  name: "ProjectSettings",
  props: {
    videosInfo: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      currencyIcon: '$',
      currentVideoId: null,
      loading: false,
      openModal: false,
      videoDetails: {},
      videos: [],
    }
  },
  computed: {
    projectId() {
      return this.$route.params.projectId;
    },
  },
  watch: {
    openModal(value) {
      if (value) {
        this.getSettings();

        if (this.currentVideoId) {
          this.videoDetails = this.videosInfo.find(el => el.id === this.currentVideoId);
        }
      }
    },
  },
  methods: {
    ...mapActions({
      error: 'error',
    }),
    getValuePerSecond(price, length) {
      if (!price || !length || typeof length === 'string') {
        return 'Not selected';
      }
      return `${this.currencyIcon} ${(price / length).toFixed(2)}`;
    },
    async getSettings() {
      const { data } = await getProjectSettings(this.projectId);
      const formatVideos = data.videos.reduce((res, cur) => {
        res[cur.video_id] = cur;
        return res;
      }, {});
      const currentVideos = this.videosInfo.map(el => ({
        ...DEFAULT_VIDEO,
        video_id: el.id,
        video_name: el.video_name,
        ...formatVideos[el.id],
      }));

      this.videos = this.currentVideoId
        ? currentVideos.filter(el => el.video_id === this.currentVideoId)
        : currentVideos;
    },
    open() {
      this.openModal = true;
    },
    reset() {
      this.currentVideoId = null;
      this.videoDetails = {};
      this.videos = [];
    },
    setInitial(id) {
      if (id) {
        this.currentVideoId = id;
      }
    },
    updateProjectInfo() {
      const formatVideos = this.videos.map(el => ({
        ...el,
        price: Number(el.price),
      }));
      return updateProjectSettings(this.projectId, formatVideos);
    },
    updateVideoInfo() {
      const { date, name, place } = this.$refs.videoInfo.getData();
      const formData = new FormData();
      formData.append('event_date', date ? moment(date).format('YYYY-MM-DD') : '');
      formData.append('event_name', name || '');
      formData.append('event_place', place || '');
      return updateVideo(formData, this.currentVideoId);
    },
    async submit() {
      this.loading = true;
      try {
        const promiseArr = [this.updateProjectInfo()];
        if (this.currentVideoId) {
          promiseArr.push(this.updateVideoInfo());
        }
        const responses = await Promise.all(promiseArr);
        const errors = responses.reduce((res, { data: { error, msg } }) => {
          if (error) {
            res.push(msg);
          }
          return res;
        }, []);

        if (errors.length) {
          errors.forEach(el => this.error(el));
        } else {
          this.$emit('update');
          this.openModal = false;
          this.reset();
        }
      } finally {
        this.loading = false;
      }
    },
  },
}
</script>

<style scoped lang="scss">
.project-form {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.form-row-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
}

.form-row-name {
  max-width: 60%;
}

.form-row-per-second {
  opacity: 0.5;
  font-size: 14px;
  margin-left: auto;
}

.form-row-content {
  display: flex;
  gap: 10px;
}

.form-field {
  flex: 1;
}

.form-field-label {
  font-weight: 700;
  padding-bottom: 7px;
  opacity: 0.5;
}

.currency {
  position: relative;

  ::v-deep {
    .el-input__inner {
      padding-left: 28px;
    }
  }
}

.currency-icon {
  position: absolute;
  top: 9px;
  left: 14px;
  z-index: 1;
  font-weight: 700;
}

.currency-select {
  max-width: 230px;
  margin-right: auto;
}

.divider {
  height: 1px;
  background: var(--border-color);
}

::v-deep {
  .el-input__inner {
    color: var(--primary);
  }
}
</style>
